// @ts-nocheck — Phase 1A: legacy code, types added incrementally
import React from "react";
// Phase 3a — migrated off window.toast onto the new Toast primitive.
// Same UX, single render surface (ToastViewport in App.tsx).
import { toast } from "../components/ui/Toast";
/* global React */
// state-storage.jsx — persistence layer extracted from app.jsx (Phase B sub-PR 1).
//
// Owns the THREE places untrusted JSON crosses into the dashboard's state:
//   1. localStorage on first mount (per-user cache)
//   2. /api/state on first mount with a view password (KV — shared state)
//   3. /api/state on every state change (debounced 1.5s; KV save)
//
// Plus the schema-version migration runner (runStateMigrations), which
// previously lived in app.jsx. It only runs on persistence-loaded payloads
// — which is now an internal concern of this module.
//
// Public API:
//   useStateStorage({ storageKey, defaultState, hubspotEnabled, viewPassword,
//                     editMode, editPassword, workerUrl })
//     → { state, setState, savedAt, kvSavedAt }
//
// Loading: <script type="text/babel" src="state-storage.jsx"> in
// Forecast.html, AFTER hooks.jsx (uses deepClone, validateOrFallback) and
// model.jsx (uses window.SCHEMAS, window.DEFAULT_STATE) and BEFORE app.jsx.

// ─── Schema version migration runner (I3 from CODE_AUDIT.md) ───────────────
// Runs only when persisted state.schemaVersion < current. Each branch upgrades
// from version N to version N+1; cascading falls through if a payload is multiple
// versions behind. Idempotent within a single version (safe to re-run if interrupted).
//
// Add new migration: bump CURRENT_SCHEMA_VERSION in model.jsx DEFAULT_STATE,
// add a branch here for the previous→new transition.
//
// Tested in tests/migrations.test.mjs (22 tests).
function runStateMigrations(state) {
  if (!state || typeof state !== "object") return state;
  const current = (window.DEFAULT_STATE && window.DEFAULT_STATE.schemaVersion) || 1;
  let v = state.schemaVersion || 1;
  if (v >= current) return state;
  // Clone-on-entry — guarantees we never mutate the caller's input even if a
  // future migration step is sloppy. Callers don't need to remember to clone.
  const next = window.deepClone(state);
  // v1 → v2: partnerUpsell renamed to setupUpsell.
  if (v < 2 && next.months) {
    Object.values(next.months).forEach(m => {
      if (m && m.partnerUpsell != null && m.setupUpsell == null) {
        m.setupUpsell = m.partnerUpsell;
        delete m.partnerUpsell;
      }
    });
    v = 2;
  }
  // v2 → v3: arpuChannels added with default. Existing users get the default.
  if (v < 3) {
    if (!Array.isArray(next.arpuChannels)) {
      next.arpuChannels = ["gtm", "selfServe", "upsells"];
    }
    v = 3;
  }
  next.schemaVersion = current;
  return next;
}

// ─── useStateStorage — the dashboard's state lifecycle hook ───────────────
// Behaviorally identical to the inline version in app.jsx pre-extraction. Three
// internal effects:
//   1. localStorage save (debounced 200ms after each state change)
//   2. /api/state KV load (once per mount when hubspotEnabled + viewPassword)
//   3. /api/state KV save (debounced 1500ms; full edit if EDIT pw, else ae-scope)
//
// The Phase A boundary validation runs at every load point (localStorage init,
// KV load); on shape failure the dashboard falls back to the previous valid
// state and surfaces a throttled toast.
function useStateStorage(opts) {
  const {
    storageKey,
    defaultState,
    hubspotEnabled,
    viewPassword,
    editMode,
    editPassword,
    workerUrl,
  } = opts;

  // Pull dependencies from window (set up by hooks.jsx + model.jsx). Defensive
  // fallbacks: if any module didn't load, we degrade rather than crash.
  const StateSchema = (window.SCHEMAS && window.SCHEMAS.StateSchema) || null;
  const validateOrFallback = window.validateOrFallback || ((schema, payload) => payload);
  const deepClone = window.deepClone || ((x) => JSON.parse(JSON.stringify(x)));

  const [state, setState] = React.useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        // R3: deepClone severs reference-sharing with the parsed JSON tree.
        // I3: runStateMigrations only acts when schemaVersion < current; idempotent.
        const parsed = runStateMigrations(deepClone(JSON.parse(saved)));
        // Phase A: validate the post-migration shape. If localStorage was
        // corrupted (manual edit / browser bug / partial write), fall back to
        // defaults and toast — never crash the dashboard on bad cached state.
        const validated = validateOrFallback(
          StateSchema, parsed, null, "localStorage", parsed && parsed.lang
        );
        if (validated) {
          const defaults = deepClone(defaultState);
          return {
            ...defaults,
            ...validated,
            months: { ...defaults.months, ...(validated.months || {}) }
          };
        }
      }
    } catch (e) { console.warn("Could not load saved state", e); }
    return deepClone(defaultState);
  });
  const [savedAt, setSavedAt] = React.useState(null);
  const [kvSavedAt, setKvSavedAt] = React.useState(null);
  const [sharedStateLoaded, setSharedStateLoaded] = React.useState(false);

  // Throttle reference for the localStorage-save toast (one per 60s per
  // browser session). Race protection ref for KV save ordering — see Effect 3.
  const lsToastSeenRef = React.useRef(0);
  const saveSeqRef = React.useRef(0);

  // ─── Effect 1: Per-user localStorage cache. Debounced 200ms. ─────────────
  // COR-2: surface storage failures via window.toast.warn so the user knows
  // their edits aren't persisting (private mode, quota exceeded). Without
  // this signal the dashboard looks fine until reload — at which point the
  // user discovers they lost everything since the last successful save.
  // Toast is throttled (one per 60s) so a user typing into a full-quota
  // disk doesn't get spammed.
  React.useEffect(() => {
    const id = setTimeout(() => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(state));
        setSavedAt(new Date());
      } catch (e) {
        console.warn("localStorage save failed:", e?.message || e);
        // Phase 3a — throttled to one per 60s per browser session. Toast
        // primitive is always loaded (module import); the null check the
        // old code had isn't needed.
        const now = Date.now();
        if ((now - lsToastSeenRef.current) > 60000) {
          lsToastSeenRef.current = now;
          toast.warn({
            title: state.lang === "it" ? "Salvataggio locale fallito" : "Local save failed",
            message: state.lang === "it"
              ? "Spazio di archiviazione pieno — le modifiche locali potrebbero non persistere fino al prossimo salvataggio remoto."
              : "Storage full — local edits may not persist until the next remote save.",
          });
        }
      }
    }, 200);
    return () => clearTimeout(id);
  }, [state, storageKey]);

  // Phase 7.5c — pick the active market slug from window.MARKET_CONFIG
  // (set by MarketDashboard before rendering ForecastItaly). Italy is the
  // default for any code path that hasn't been threaded through yet.
  const marketSlug = (typeof window !== "undefined" && (window as any).MARKET_CONFIG?.slug) || "italy";
  const marketQs = `?market=${encodeURIComponent(marketSlug)}`;

  // ─── Effect 2: KV state load on first mount with view password. ──────────
  // Shared state sync via KV: load on mount, save on edit (only if editMode +
  // EDIT password). localStorage stays as a per-user cache for offline /
  // initial-paint speed.
  React.useEffect(() => {
    if (!hubspotEnabled || !viewPassword || sharedStateLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${workerUrl}/api/state${marketQs}`, { headers: { "X-View-Password": viewPassword } });
        if (cancelled) return;
        if (resp.ok) {
          // R3: deepClone severs the response-body reference so mutations don't bleed.
          // I3: same migration runner as localStorage; only acts on out-of-date schemas.
          const remote = runStateMigrations(deepClone(await resp.json()));
          // Phase A: validate the KV payload. If the worker returned a malformed
          // state (drift between frontend and worker schema, mid-deploy partial
          // write), keep the localStorage-hydrated state and toast.
          const validated = validateOrFallback(
            StateSchema, remote, null, "kvState", (remote && remote.lang) || state.lang
          );
          if (validated && typeof validated === "object" && validated.months) {
            const defaults = deepClone(defaultState);
            setState(s => ({
              ...defaults,
              ...validated,
              months: { ...defaults.months, ...(validated.months || {}) },
            }));
          }
        }
      } catch (e) { /* offline or worker down — keep localStorage state */ }
      finally { if (!cancelled) setSharedStateLoaded(true); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally
    // omitting `state.lang` and `defaultState`/`StateSchema`/`validateOrFallback`/
    // `deepClone` from deps. `state.lang` is captured at first render (used only
    // for toast fallback language); the others are window-scoped constants that
    // don't change at runtime. Including them would re-trigger the load.
  }, [hubspotEnabled, viewPassword, sharedStateLoaded, workerUrl]);

  // ─── Effect 3: KV state save on every state change (debounced 1500ms). ───
  // - In full EDIT mode: send X-Edit-Password → Worker accepts the whole state.
  // - In VIEW mode: send X-Edit-Scope: ae → Worker keeps current state and merges in only
  //   `aes` and `months[*].aes` fields (the only ones the UI lets a VIEW user change).
  // Race protection (R1 from CODE_AUDIT): each save is tagged with a monotonic seq.
  // If a slower save A finishes after a faster save B, A's "savedAt" timestamp is
  // ignored — B already committed the newer state. Without this, on flaky connections
  // the UI could flash an older "saved" timestamp than the actual KV state.
  React.useEffect(() => {
    if (!hubspotEnabled || !sharedStateLoaded) return;
    // Phase 6.2 — read-only invariant: while an admin is watching another
    // user, skip the KV save entirely so writes attributed to the watch
    // target never land in shared state. The local in-memory state still
    // mutates (so the admin sees what edits would look like), but nothing
    // is persisted. Exiting watch resumes saves on the next mutation.
    try {
      if (localStorage.getItem("forecast_watch_as_id")) return;
    } catch {/* no storage → behave as normal */}
    const isFullEditMode = editMode === true && editPassword;
    const id = setTimeout(async () => {
      const mySeq = ++saveSeqRef.current;
      try {
        const headers = {
          "Content-Type": "application/json",
          "X-View-Password": viewPassword,
        };
        if (isFullEditMode) headers["X-Edit-Password"] = editPassword;
        else headers["X-Edit-Scope"] = "ae";
        // Phase 5.6 — scope:ae now requires X-User-Id; worker resolves the
        // operator and filters the merge to AE rows they own. Admins
        // bypass the filter.
        try {
          const opId = localStorage.getItem("forecast_active_user_id");
          if (opId) headers["X-User-Id"] = opId;
        } catch { /* private mode / no storage — let the worker 403 */ }
        const resp = await fetch(`${workerUrl}/api/state${marketQs}`, {
          method: "PUT",
          headers,
          body: JSON.stringify(state),
        });
        // Only mark savedAt if we're still the latest — out-of-order responses ignored.
        if (resp.ok && mySeq === saveSeqRef.current) setKvSavedAt(new Date());
      } catch (e) { /* network glitch — local cache still has the data */ }
    }, 1500);
    return () => clearTimeout(id);
  }, [state, editMode, editPassword, sharedStateLoaded, hubspotEnabled, viewPassword, workerUrl]);

  return { state, setState, savedAt, kvSavedAt };
}

// ─── Publish to window for app.jsx ─────────────────────────────────────────
Object.assign(window, { useStateStorage, runStateMigrations });
window.STATE_STORAGE = { useStateStorage, runStateMigrations };
